/**
 * 入住人模块
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { Button, message } from 'antd';
import cls from 'classnames';
import intl from 'react-intl-universal';

import ContentBlock, { FormItem } from '@/components/Reservation/ContentBlock';
import usePortalConfirm from '@/components/PortalConfirm';
import CardRecharge, { IcardRechargeRef } from '@/components/CardRecharge';
import CardPurchase, { IcardPurchaseRef, PurchaseResult } from '@/components/CardPurchase';
import checkInBase from '@/api/checkInBase';
import { fetchMemberBusinessInfo } from '@/api/payment';
import useSafeState from '@/hooks/useSafeState';
import DateJS, { format2FullTimeStr, format2TimeStamp } from '@/utils/date';
import Card from '@/utils/card';
import {
  CertIDType,
  ID_STANDING_DATE,
  MemberType,
  MEMBER_TYPE_MAP,
  PAYMENT_BUSINESS_CODE,
  SEX_VALUE_SYMBOL,
  MainOrderStatus,
} from '@/config/constant';
import { CheckInGuestInfo, NoticeInfo } from '@/redux/reducers/reservation/base.d';
import { findItemByKey } from '@/redux/actions/reservation/helper';
import { MemberLevelItem } from '@/redux/reducers/guest';
import { isNil } from '@/utils/number';
import { fetchCardInfoByWyuid, fetchUserAvailbleCardList } from '@/api/guest';
import { isStandingCardTime } from '@/utils/transform';
import useOrderLink from '@/hooks/useOrderLink';
import { formatAreaMobileStr } from '@/components/BaseMobileInput/helper';

import CustomerItem from './CustomerItem';
import DetailModal, { DetailModalRef, IDCardDetailInfo } from './DetailModal';

import styles from './index.module.scss';

interface IProps {
  isMasterReadOnly: boolean; // 房主只读
  autoCheckReserve: boolean; // 是否自动查询预订单
  className?: string;
  guestList: CheckInGuestInfo[]; // 宾客列表
  memberLevelMap: { [key: string]: MemberLevelItem };
  onAddItem: (data: Partial<CheckInGuestInfo>, callback: (item: CheckInGuestInfo) => void) => void; // 新增
  onItemDelete: (id: string) => void; // 删除
  onItemChange: (id: string, data: Partial<CheckInGuestInfo>) => void; // 修改
  onItemClear: (id: string) => void; // 清空
  onSetToOwner: (id: string) => void; // 设为房主
  getItemList: (callback: (list: CheckInGuestInfo[]) => void) => void; // 获取所有宾客列表
  setNoticeContent: (notice: NoticeInfo) => void; // 设置证件读取信息
  getPopContainer: () => HTMLElement; // popup 容器
  setLoadingStatus: (visible: boolean) => void; // loading
  onIDCardReadSuccess: () => void; // 身份证读取回调
}

interface OriginCardReadData {
  address: string; // 身份证地址
  agency: string; // 签发机关
  birthdate: string; // 生日 19920223
  effecteddate: string; // 有效期开始日期 19920223
  expireddate: string; // 有效期截至日期 19920223
  id: string; // 身份证号
  name: string; // 姓名
  nation: string; // 民族
  sex: '男' | '女'; // 性别 男女
  // type: '居民身份证';
}

type CardReadResult = Pick<CheckInGuestInfo, keyof IDCardDetailInfo | 'source' | 'certType'>;
// 性别转换
const SEX_VALUE_MAP = {
  男: SEX_VALUE_SYMBOL.Male,
  女: SEX_VALUE_SYMBOL.Female,
};
// 匿名用户
const AnonymousNameSymbol = '待输入姓名';

/**
 * 获取证件相关时间戳
 * 如果不是合法的时间 返回原始值
 * fix：2024-06-03 都按东八区处理
 */
const getCardTimeStamp = (val) => {
  if (val) {
    const date = DateJS.utc(val, 'YYYYMMDD');
    return date.isValid() ? date.subtract(8, 'hour').valueOf() : null;
  }
  return null;
};

// 证件扫描
let socketInstance: Card;
const clearConnect = () => {
  if (socketInstance) {
    // 只保留一个读取服务
    try {
      socketInstance.close();
    } catch (_) {
      //
    }
    socketInstance = null;
  }
};
const readIdCard = () => {
  clearConnect();
  return new Promise<CardReadResult>((resolve) => {
    const card = new Card();
    socketInstance = card;
    const readCardInfo = () => {
      card
        .readIdCard(false)
        .then((data: OriginCardReadData) => {
          const {
            name,
            sex,
            nation,
            birthdate,
            address,
            id,
            agency,
            effecteddate,
            expireddate,
          } = data;
          resolve({
            name,
            sex: SEX_VALUE_MAP[sex] ?? SEX_VALUE_SYMBOL.Unknown, // 没有匹配到 就是未知
            nation,
            birthday: getCardTimeStamp(birthdate),
            certificateLocation: address,
            certNo: id,
            issuingAuthority: agency,
            certificateStartTime: getCardTimeStamp(effecteddate),
            certificateEndTime: isStandingCardTime(expireddate)
              ? ID_STANDING_DATE
              : getCardTimeStamp(expireddate),
            // 默认为身份证
            certType: CertIDType.ID_CARD,
            source: 'READ', // 数据来源为读取
          });
          card.close();
          socketInstance = null;
        })
        .catch(() => {
          readCardInfo();
        });
    };
    socketInstance.socket.addEventListener('open', () => {
      readCardInfo();
    });
    socketInstance.socket.addEventListener('error', () => {
      clearConnect();
    });
  });
};

const getInfoByScan: (func: (value: CardReadResult) => void) => void = (func) =>
  readIdCard().then(func);

const Index: React.FC<IProps> = (props) => {
  const {
    isMasterReadOnly,
    className,
    guestList,
    memberLevelMap,
    getPopContainer,
    setNoticeContent,
    setLoadingStatus,
    autoCheckReserve,
    onAddItem,
    onItemDelete,
    onItemChange,
    onItemClear,
    onSetToOwner,
    getItemList,
    onIDCardReadSuccess,
  } = props;
  const cardPurchaseRef = useRef<IcardPurchaseRef>(null);
  const cardRechargeRef = useRef<IcardRechargeRef>(null);
  const purchaseCallbackRef = useRef<(result: PurchaseResult) => void>(null);
  const rechargeCallbackRef = useRef<(isSuccess: boolean) => void>(null);
  const [billConfig, setBillConfig] = useSafeState({
    rechargeAble: false,
    cardPurchaseAble: false,
  });
  const [canBuyCardMap, setCanBuyCardMap] = useSafeState({});
  const detailModalRef = useRef<DetailModalRef>(null);
  const portalConfirm = usePortalConfirm();
  const { linkToReservationList } = useOrderLink();

  useEffect(() => {
    fetchMemberBusinessInfo().then((res) => {
      if (res && res.data) {
        let rechargeAble = false; // 充值是否开启
        let cardPurchaseAble = false; // 会员卡购买是否开始
        (res.data || []).forEach((item) => {
          if (item.businessCode === PAYMENT_BUSINESS_CODE.MEMBER_STORED_VALUE) {
            rechargeAble = item.status === 0;
          }
          if (item.businessCode === PAYMENT_BUSINESS_CODE.MEMBER_CARD_BUY) {
            cardPurchaseAble = item.status === 0;
          }
        });
        setBillConfig({
          rechargeAble, // 充值是否开启
          cardPurchaseAble, // 会员卡购买是否开始
        });
      }
    });
    return () => {
      // 关闭socket
      clearConnect();
    };
  }, []);

  const checkCanBuyCard = (wyuid) => {
    // 校验是否可购卡
    if (wyuid) {
      fetchUserAvailbleCardList({ wyuIds: [wyuid] }).then((cardRes) => {
        if (cardRes?.code === 0) {
          setCanBuyCardMap((pre) => ({ ...pre, ...cardRes?.data }));
        }
      });
    }
  };

  const checkItemMemberStatus: (indexId: string, isMatser?: boolean) => void = (
    indexId,
    isMatser,
  ) => {
    return new Promise<void>((resolve) => {
      getItemList(async (list) => {
        const targetItem = findItemByKey(list, 'indexId', indexId);
        const { certType, name, certNo } = targetItem || {};
        if (certNo) {
          setLoadingStatus(true);
          try {
            let params: any = { name: name || AnonymousNameSymbol, certType, certNo };
            if (targetItem.source === 'READ') {
              // 读取的证件
              const {
                birthday,
                nation,
                certificateLocation,
                issuingAuthority,
                certificateStartTime,
                certificateEndTime,
              } = targetItem;
              params = {
                ...params,
                nation,
                birthday,
                certificateLocation,
                issuingAuthority,
                certificateStartTime,
                certificateEndTime,
              };
            }

            let res = null;
            // 房主且证件号脱敏，更新手机号、邮箱号、会员等级
            if (isMatser && certNo.includes('*')) {
              res = await fetchCardInfoByWyuid({ wyuid: targetItem.wyuId }).catch((e) => {
                throw e;
              });
              targetItem.mobile = res.data?.mobile;
              targetItem.email = res.data?.email;
            } else {
              res = await checkInBase.getMemberInfo(params).catch((e) => {
                const hackCode = [101920012, 102020011]; // 只报这几个code的错 临时方案
                if (hackCode.includes(e?.code) && e?.msg) {
                  message.error(e.msg);
                }
                throw e;
              });
            }
            const resData = res.data;
            let finalName = params.name; // 默认为输入的姓名
            if (params.name === AnonymousNameSymbol) {
              // 查询的结果是特定的名称 显示为空
              finalName = resData.name === AnonymousNameSymbol ? '' : resData.name;
            }
            // 校验是否可购卡
            checkCanBuyCard(resData.wyuid);

            onItemChange(indexId, {
              name: finalName,
              remark: resData.remark,
              mobile: targetItem.mobile || resData.mobile,
              email: targetItem.email || resData.email,
              wyuId: resData.wyuid,
              memberLevel: resData.memberLevel,
              balance: !isNil(resData.memberLevel) ? resData.balance : undefined,
              accountType: resData.accountType,
              certType: resData.certType,
            });
          } catch (e) {
            // 请求出错 需要清空原会员信息
            onItemChange(indexId, {
              wyuId: undefined,
              memberLevel: undefined,
              balance: '',
            });
          }
          setLoadingStatus(false);
          resolve();
        } else {
          resolve();
        }
      });
    });
  };

  // 校验是否有预订单
  const autoCheckOrder: (indexId: string) => void = (indexId) => {
    if (!autoCheckReserve) return;
    getItemList(async (list) => {
      const targetItem = findItemByKey(list, 'indexId', indexId);
      if (targetItem && targetItem.certNo) {
        const { name, mobile, certNo } = targetItem;
        try {
          const res = await checkInBase.getOrderByIDCard({
            name,
            mobile,
            certNo,
          });
          if (res.data) {
            const orderInfo = res.data;
            portalConfirm.showDrawer({
              title: intl.get('drawer_checkin_guest_reserve_title'),
              content: (
                <div>
                  <div>{intl.get('drawer_checkin_guest_reserve_content')}</div>
                  <FormItem label={intl.get('app_constants_booker_name')}>
                    {orderInfo.name || '—'}
                  </FormItem>
                  <br />
                  <FormItem label={intl.get('app_reservation_phone_number')}>
                    {formatAreaMobileStr(orderInfo.mobile)}
                  </FormItem>
                  <br />
                  <FormItem label={intl.get('help_store_address_type_email')}>
                    {orderInfo.email || '—'}
                  </FormItem>
                  <br />
                  <FormItem label={intl.get('drawer_checkin_guest_reserve_create_time')}>
                    {orderInfo.createTime ? format2FullTimeStr(orderInfo.createTime) : '—'}
                  </FormItem>
                  <br />
                  <FormItem label={intl.get('drawer_checkin_guest_reserve_order_no')}>
                    {orderInfo.resvNo}
                  </FormItem>
                </div>
              ),
              cancelText: intl.get('app_confirm_cancel_btn'),
              onCancel: () => {
                portalConfirm.closeDrawer();
              },
              confirmText: intl.get('drawer_checkin_guest_reserve_detail_btn'),
              onConfirm: () => {
                portalConfirm.closeDrawer();
                linkToReservationList(MainOrderStatus.ReservationOrder);
              },
            });
          }
        } catch (e) {
          //
        }
      }
    });
  };

  // 刷证件 读取是否有预订 和会员信息
  const autoCheckInfo: (indexId: string) => Promise<void> = async (indexId) => {
    await checkItemMemberStatus(indexId);
    autoCheckOrder(indexId);
  };

  // 增加入住人
  const addCustomer = () => {
    onAddItem({ memberType: MemberType.CheckIn }, (item) => {
      const itemId = item.indexId;
      // 发起身份证读取
      getInfoByScan((result) => {
        onItemChange(itemId, result);
        setNoticeContent({
          type: 'success',
          message: intl.get('drawer_checkin_notice_cert_read_done', {
            role: MEMBER_TYPE_MAP[MemberType.CheckIn],
          }),
        });
        autoCheckInfo(itemId);
        message.success(intl.get('drawer_checkin_message_cert_read_success'));
        onIDCardReadSuccess();
      });
      setNoticeContent({
        type: 'info',
        message: intl.get('drawer_checkin_notice_cert_read_doing', {
          role: MEMBER_TYPE_MAP[MemberType.CheckIn],
        }),
      });
    });
  };

  useEffect(() => {
    const lastGuestInfo = guestList ? guestList[guestList.length - 1] : null;
    if (!lastGuestInfo) return; // 宾客
    const guestTypeName = MEMBER_TYPE_MAP[lastGuestInfo.memberType];
    const itemId = lastGuestInfo.indexId;
    // 读取最后一个宾客的身份证
    getInfoByScan((result) => {
      onItemChange(itemId, result);
      setNoticeContent({
        type: 'success',
        message: intl.get('drawer_checkin_notice_cert_read_done', { role: guestTypeName }),
      });
      autoCheckInfo(itemId);
      message.success(intl.get('drawer_checkin_message_cert_read_success'));
      onIDCardReadSuccess();
    });
    setNoticeContent({
      type: 'info',
      message: intl.get('drawer_checkin_notice_cert_read_doing', { role: guestTypeName }),
    });
  }, []);

  const { masterInfoData, guestListArr } = useMemo(() => {
    let masterInfo: CheckInGuestInfo;
    const guestListResult: CheckInGuestInfo[] = [];
    guestList.forEach((item) => {
      // 房主
      if (item.memberType === MemberType.HouseOwner) {
        masterInfo = item;
      } else {
        guestListResult.push(item);
      }
    });
    return {
      masterInfoData: masterInfo,
      guestListArr: guestListResult,
    };
  }, [guestList]);

  useEffect(() => {
    checkCanBuyCard(masterInfoData?.wyuId);
  }, [masterInfoData]);

  // 会员卡购买回调
  const handlePurchaseCallback = (result) => {
    const callback = purchaseCallbackRef.current;
    if (typeof callback === 'function') {
      callback(result);
    }
  };

  // 购买会员卡
  const showCardPurchaseModal = (data, isMatser?) => {
    getItemList(() => {
      // const masterInfo = findItemByKey(list, 'memberType', MemberType.HouseOwner);
      // if (masterInfo) {
      const { certType, certNo, name, mobile, email, wyuId: wyuid } = data;
      // 会员卡购买回调
      purchaseCallbackRef.current = (result) => {
        const masterUserId = data?.indexId;
        if (result.isBuySuccess && masterUserId) {
          onItemChange(masterUserId, {
            name: result.name,
            certType: result.certType,
            certNo: result.certNo,
            mobile: result.mobile,
            email: result.email,
          });
          checkItemMemberStatus(masterUserId, isMatser);
        }
      };
      cardPurchaseRef.current.openDrawer({
        isNew: false,
        name,
        certType,
        certNo,
        mobile,
        wyuid,
        email,
      });
      // }
    });
  };

  // 充值回调
  const handleRechargeCallback = (result) => {
    const callback = rechargeCallbackRef.current;
    if (typeof callback === 'function') {
      callback(result);
    }
  };

  // 充值
  const showCardRechargeModal = (data, isMatser?) => {
    getItemList(() => {
      // 房主
      // const masterInfo = findItemByKey(list, 'memberType', MemberType.HouseOwner);
      // if (masterInfo) {
      const { wyuId, accountType, indexId, certType, certNo, name, mobile, email } = data;
      // 充值成功回调
      rechargeCallbackRef.current = (isSuccess) => {
        if (isSuccess && indexId) {
          checkItemMemberStatus(indexId, isMatser);
        }
      };
      cardRechargeRef.current.openDrawer({
        isNew: false,
        wyuid: wyuId,
        accountType,
        certType,
        certNo,
        name,
        mobile,
        email,
      });
      // }
    });
  };

  // 显示宾客详情弹框
  const showGuestDetailModal = (data: IDCardDetailInfo) => {
    detailModalRef.current.openModal(data);
  };

  // 房主信息
  const renderMasterInfo = () => {
    if (!masterInfoData) return null;
    return (
      <div>
        <div className={styles.roleTag}>{intl.get('app_constants_master_name')}</div>
        <CustomerItem
          isMatser
          memberLevelMap={memberLevelMap}
          readOnly={isMasterReadOnly}
          showRecharge={billConfig.rechargeAble}
          showCardPurchase={billConfig.cardPurchaseAble}
          canBuyCardMap={canBuyCardMap}
          data={masterInfoData}
          key={masterInfoData.indexId}
          getPopContainer={getPopContainer}
          onChange={onItemChange}
          refreshCustomer={onItemClear}
          checkMemberById={(data) => checkItemMemberStatus(data, true)}
          showCardPurchaseModal={(data) => showCardPurchaseModal(data, true)}
          showCardRechargeModal={(data) => showCardRechargeModal(data, true)}
          showGuestDetailModal={showGuestDetailModal}
        />
      </div>
    );
  };

  return (
    <ContentBlock
      title={intl.get('drawer_checkin_guest_block_title')}
      className={cls(styles.guestListContainer, className)}
    >
      <div className={styles.listWrapper}>
        {renderMasterInfo()}
        {guestListArr.length ? (
          <div>
            <div className={styles.roleTag}>{intl.get('app_constants_occupant_name')}</div>
            {guestListArr.map((item) => {
              return (
                <CustomerItem
                  memberLevelMap={memberLevelMap}
                  readOnly={false}
                  showRecharge={billConfig.rechargeAble}
                  showCardPurchase={billConfig.cardPurchaseAble}
                  canBuyCardMap={canBuyCardMap}
                  data={item}
                  key={item.indexId}
                  onChange={onItemChange}
                  getPopContainer={getPopContainer}
                  setToMaster={isMasterReadOnly ? undefined : onSetToOwner}
                  deleteCustomer={onItemDelete}
                  checkMemberById={checkItemMemberStatus}
                  showCardPurchaseModal={showCardPurchaseModal}
                  showCardRechargeModal={showCardRechargeModal}
                  showGuestDetailModal={showGuestDetailModal}
                />
              );
            })}
          </div>
        ) : null}
      </div>
      <Button onClick={addCustomer} type="primary" ghost>
        {intl.get('drawer_checkin_guest_add_btn')}
      </Button>
      <CardPurchase ref={cardPurchaseRef} onCloseDrawer={handlePurchaseCallback} />
      <CardRecharge ref={cardRechargeRef} onCloseDrawer={handleRechargeCallback} />
      <DetailModal ref={detailModalRef} />
    </ContentBlock>
  );
};

export default Index;
